import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, latestComics, featuredComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';
import { useRef } from 'react';

function getComicsByCategory(slug: string): Comic[] {
  const allComics = [...featuredComics, ...latestComics];
  return allComics.filter((c) => c.categorySlug === slug);
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const comics = getComicsByCategory(slug || '');
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-foreground-600 text-lg">分类未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name}韩漫_免费${category.name}韩国漫画在线观看-韩国漫画网`;
  const pageDesc = `${category.description}韩国漫画网为您精选海量${category.name}韩漫作品，每日更新，高清画质，支持手机在线观看。`;

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -320 : 320,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {/* Page meta */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${category.name}韩漫,${category.name}韩国漫画,免费${category.name}漫画,韩漫在线观看,韩国漫画网`} />
      <link rel="canonical" href={`http://acwaterpump.com/category/${slug}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={`http://acwaterpump.com/category/${slug}`} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: pageTitle,
        description: pageDesc,
        url: `http://acwaterpump.com/category/${slug}`,
        isPartOf: { '@type': 'WebSite', name: '韩国漫画网', url: 'http://acwaterpump.com/' },
      })}</script>

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: category.name },
          ]} />

          {/* Category Header */}
          <section className="flex flex-col lg:flex-row gap-10 py-10 md:py-16">
            {/* Left - featured image */}
            <div className="w-full lg:w-1/3">
              <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-300 text-foreground-700 text-xs font-medium mb-4">
                {category.name}
              </span>
              <div className="rounded-3xl overflow-hidden aspect-[3/4]">
                <img
                  src={comics.length > 0 ? comics[0].coverImage : 'https://readdy.ai/api/search-image?query=Korean%20webtoon%20art%20collection%20display%2C%20vibrant%20colorful%20manga%20style%20illustrations%2C%20clean%20simple%20background%2C%20beautiful%20character%20designs%2C%20soft%20lighting%2C%20professional%20quality&width=800&height=1067&seq=category-default-01&orientation=portrait'}
                  alt={`${category.name}韩国漫画`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right - info */}
            <div className="w-full lg:w-2/3 flex flex-col justify-center">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                {category.name}
              </h1>
              <p className="mt-6 text-foreground-600 text-sm md:text-base leading-relaxed max-w-xl">
                {category.description}
              </p>
              <Link
                to={`/comic/${comics.length > 0 ? comics[0].id : ''}`}
                className="mt-8 self-start whitespace-nowrap px-8 py-3.5 rounded-xl bg-foreground-950 text-background-50 font-medium text-sm cursor-pointer hover:bg-foreground-800 transition-colors"
              >
                立即阅读热门作品
              </Link>
            </div>
          </section>

          {/* Comics Grid */}
          <section className="pb-16 md:pb-24">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                {category.name}作品列表
              </h2>
              <div className="hidden md:flex items-center gap-3">
                <button
                  onClick={() => scroll('left')}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-foreground-300 text-foreground-600 cursor-pointer hover:border-foreground-500 transition-colors"
                  aria-label="向左滚动"
                >
                  <i className="ri-arrow-left-line text-lg"></i>
                </button>
                <button
                  onClick={() => scroll('right')}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 cursor-pointer hover:bg-foreground-800 transition-colors"
                  aria-label="向右滚动"
                >
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </div>
            </div>

            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto scroll-smooth pb-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none' }}
            >
              {comics.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex-shrink-0 w-[260px] snap-start group cursor-pointer"
                  data-product-shop=""
                >
                  <div className="relative rounded-xl overflow-hidden bg-background-50">
                    {index === 0 && (
                      <span className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-md bg-accent-500 text-foreground-950 text-xs font-medium">
                        推荐
                      </span>
                    )}
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.coverImage}
                        alt={`${comic.title} - ${category.name}`}
                        title={`${comic.title} - ${category.name}韩漫在线阅读`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base font-semibold text-foreground-950 whitespace-nowrap overflow-hidden text-ellipsis">
                        {comic.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-foreground-600">{comic.latestChapter}</p>
                      <p className="mt-0.5 text-xs text-foreground-500">{comic.updateDate}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* CTA Section */}
        <section className="relative w-full h-[420px] md:h-[500px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20Korean%20webtoon%20style%20illustration%2C%20soft%20dreamy%20atmosphere%20with%20pastel%20colors%2C%20flowing%20artistic%20abstract%20shapes%2C%20warm%20coral%20and%20cream%20tones%2C%20cinematic%20wide%20composition%2C%20airy%20and%20elegant%2C%20modern%20minimalist%20design&width=1600&height=900&seq=cta-category-01&orientation=landscape"
            alt="订阅韩国漫画更新"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>

          <div className="relative z-10 w-full h-full max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col items-start justify-center">
            <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white max-w-2xl leading-tight">
              发现更多
              <br />
              {category.name}
            </h2>
            <Link
              to="/category/romance"
              className="mt-10 whitespace-nowrap px-8 py-3.5 rounded-full border border-white/60 text-white font-medium text-sm cursor-pointer hover:bg-white/10 transition-colors inline-flex items-center gap-3"
            >
              <span>浏览全部漫画</span>
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white text-foreground-950">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}